// client/src/components/publishing/RssFeedsPage.jsx
//
// /:tenant/media/rss-feeds — RSS auto-posting (Roadmap Phase 3.4). Owners
// and admins follow a blog or news feed; new items become posts:
//   Draft for review  -> a draft in Approvals, nothing publishes by itself
//   Schedule          -> queued to publish a set time after the item appears
//                        (still through the approval policy)
// What is already in the feed when it's added is never posted. The worker
// checks each feed about every 30 minutes and posts at most 3 new items per
// check.
//
// API: /api/publishing/:tenant/rss* (server/routes/publishingRoutes.js).

import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaFileCsv, FaPause, FaPlay, FaPlus, FaTrash } from 'react-icons/fa';
import AppFooter from '../AppFooter';
import DesktopActionBar from '../DesktopActionBar';
import LoadingState from '../common/LoadingState';
import {
  listFeeds, previewFeed, addFeed, updateFeed, removeFeed, feedItems, errorText,
} from '../../services/publishingApi';
import './Publishing.css';

const NAMES = { twitter: 'X (Twitter)', bluesky: 'Bluesky', threads: 'Threads', instagram: 'Instagram', linkedin: 'LinkedIn', facebook: 'Facebook' };
const DELAYS = [0, 30, 60, 180, 1440];

function when(value) {
  if (!value) return '';
  const d = new Date(String(value).replace(' ', 'T') + (String(value).includes('T') ? '' : 'Z'));
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
}

const EMPTY = { url: '', mode: 'draft', delayMinutes: 60, platforms: ['facebook'], template: '{title} {link}', includeImage: true };

export default function RssFeedsPage({ tenantId: tenantProp }) {
  const { tenant: tenantParam } = useParams();
  const tenant = tenantParam || tenantProp;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(EMPTY);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState('');
  const [items, setItems] = useState({});
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    try {
      setData(await listFeeds(tenant));
    } catch (err) {
      if (err?.response?.status === 403) setDenied(true);
      else setError(errorText(err, t('publishing.rss.loadError', 'Could not load your feeds.')));
    }
  }, [tenant, t]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); if (k === 'url') setPreview(null); };
  const togglePlatform = (p) => set('platforms', form.platforms.includes(p) ? form.platforms.filter((x) => x !== p) : [...form.platforms, p]);

  const check = async () => {
    setBusy('preview'); setError(''); setPreview(null);
    try {
      setPreview(await previewFeed(tenant, { url: form.url, template: form.template, platforms: form.platforms }));
    } catch (err) {
      setError(errorText(err, t('publishing.rss.previewError', 'We could not read that feed.')));
    } finally { setBusy(''); }
  };

  const add = async (e) => {
    e.preventDefault();
    setBusy('add'); setError(''); setNotice('');
    try {
      const r = await addFeed(tenant, form);
      setNotice(t('publishing.rss.added', 'Following “{{title}}”. The {{count}} item(s) already in it will not be posted; new ones will.', { title: r.feed.title || r.feed.url, count: r.seeded }));
      setForm(EMPTY); setPreview(null);
      await load();
    } catch (err) {
      setError(errorText(err, t('publishing.rss.addError', 'The feed could not be added.')));
    } finally { setBusy(''); }
  };

  const toggleActive = async (feed) => {
    setError('');
    try { await updateFeed(tenant, feed.id, { active: !feed.active }); await load(); } catch (err) { setError(errorText(err, t('publishing.rss.updateError', 'Could not change the feed.'))); }
  };
  const remove = async (feed) => {
    if (!window.confirm(t('publishing.rss.confirmRemove', 'Stop following “{{title}}”? Posts already made from it stay.', { title: feed.title || feed.url }))) return;
    try { await removeFeed(tenant, feed.id); await load(); } catch (err) { setError(errorText(err, t('publishing.rss.updateError', 'Could not change the feed.'))); }
  };
  const showItems = async (feed) => {
    if (items[feed.id]) { setItems((m) => ({ ...m, [feed.id]: null })); return; }
    try { const r = await feedItems(tenant, feed.id); setItems((m) => ({ ...m, [feed.id]: r.items })); } catch (err) { setError(errorText(err, t('publishing.rss.loadError', 'Could not load your feeds.'))); }
  };

  const outcome = {
    posted: t('publishing.rss.outcomePosted', 'Post made'),
    seeded: t('publishing.rss.outcomeSeeded', 'Already in the feed'),
    skipped: t('publishing.rss.outcomeSkipped', 'Skipped'),
  };
  const delayLabel = (m) => (m === 0 ? t('publishing.rss.delayNow', 'As soon as it’s found')
    : m < 60 ? t('publishing.rss.delayMinutes', '{{count}} minutes later', { count: m })
      : m < 1440 ? t('publishing.rss.delayHours', '{{count}} hour(s) later', { count: m / 60 })
        : t('publishing.rss.delayDay', 'The next day'));

  const buttons = [
    { icon: <FaArrowLeft />, label: t('common.back', 'Back'), onClick: () => navigate(-1) },
    { icon: <FaFileCsv />, label: t('publishing.bulk.navLabel', 'Bulk schedule'), onClick: () => navigate(`/${tenant}/media/bulk-schedule`) },
  ];
  const platforms = (data && data.platforms) || Object.keys(NAMES);
  const atLimit = data && data.feeds && data.feeds.length >= data.limit;

  return (
    <div className="pb-page">
      <DesktopActionBar buttons={buttons} />
      <section className="surface">
        <h2>{t('publishing.rss.title', 'RSS feeds')}</h2>
        <p className="surface-muted">
          {t('publishing.rss.intro', 'Follow your blog, newsletter or news page. When it publishes something new, we turn it into a post: as a draft for you to review, or scheduled automatically. Items already in the feed are never posted.')}
        </p>
      </section>

      {denied && <div className="note warn" role="status">{t('publishing.rss.denied', 'Only owners and admins can manage RSS feeds.')}</div>}
      {!denied && !data && !error && <LoadingState />}
      {error && <div className="note error" role="alert">{error}</div>}
      {notice && <div className="note ok" role="status">{notice}</div>}
      {data && !data.ready && <div className="note warn" role="status">{t('publishing.rss.migration', 'RSS feeds need a database update before they can be used.')}</div>}

      {data && data.ready && (
        <section className="surface">
          <h3>{t('publishing.rss.addTitle', 'Add a feed')}</h3>
          {atLimit ? (
            <p className="surface-muted">{t('publishing.rss.atLimit', 'You follow {{count}} feeds, the most your account allows. Remove one to add another.', { count: data.limit })}</p>
          ) : (
            <form className="pb-form" onSubmit={add} data-testid="rss-form">
              <label className="pb-field pb-wide" htmlFor="pb-rss-url">
                {t('publishing.rss.url', 'Feed address')}
                <input id="pb-rss-url" type="url" required value={form.url} onChange={(e) => set('url', e.target.value)} placeholder="https://example.org/feed" />
              </label>
              <fieldset className="pb-field pb-wide" style={{ border: 0, padding: 0, margin: 0 }}>
                <legend>{t('publishing.rss.platforms', 'Post to')}</legend>
                <div className="pb-checks">
                  {platforms.map((p) => (
                    <label key={p}>
                      <input type="checkbox" checked={form.platforms.includes(p)} onChange={() => togglePlatform(p)} /> {NAMES[p] || p}
                    </label>
                  ))}
                </div>
                {form.platforms.includes('instagram') && (
                  <span className="pb-ai-meta">{t('publishing.rss.igNote', 'Instagram needs a photo: items without one skip Instagram.')}</span>
                )}
              </fieldset>
              <label className="pb-field" htmlFor="pb-rss-mode">
                {t('publishing.rss.mode', 'New items become')}
                <select id="pb-rss-mode" value={form.mode} onChange={(e) => set('mode', e.target.value)}>
                  <option value="draft">{t('publishing.rss.modeDraft', 'Drafts for me to review')}</option>
                  <option value="schedule">{t('publishing.rss.modeSchedule', 'Scheduled posts')}</option>
                </select>
              </label>
              {form.mode === 'schedule' && (
                <label className="pb-field" htmlFor="pb-rss-delay">
                  {t('publishing.rss.delay', 'Publish')}
                  <select id="pb-rss-delay" value={form.delayMinutes} onChange={(e) => set('delayMinutes', Number(e.target.value))}>
                    {DELAYS.map((m) => <option key={m} value={m}>{delayLabel(m)}</option>)}
                  </select>
                </label>
              )}
              <label className="pb-field pb-wide" htmlFor="pb-rss-template">
                {t('publishing.rss.template', 'Post text')}
                <input id="pb-rss-template" type="text" maxLength={500} value={form.template} onChange={(e) => set('template', e.target.value)} />
                <span className="pb-ai-meta">{t('publishing.rss.templateHelp', 'Use {title}, {link}, {summary} and {feed}. Long text is shortened to fit each platform; the link is kept.')}</span>
              </label>
              <label className="pb-checks pb-wide">
                <input type="checkbox" checked={form.includeImage} onChange={(e) => set('includeImage', e.target.checked)} />
                {t('publishing.rss.image', 'Add the item’s image when it has one')}
              </label>
              {preview && (
                <div className="pb-preview pb-wide" data-testid="rss-preview">
                  <strong>{preview.title || preview.url}</strong> · {t('publishing.rss.itemCount', '{{count}} item(s) now', { count: preview.itemCount })}
                  {preview.sample && <p style={{ margin: '6px 0 0' }}>{t('publishing.rss.sample', 'A new item would be posted as:')} <em>{preview.sample}</em></p>}
                  <ul>{preview.items.map((it) => <li key={it.link || it.title}>{it.title}</li>)}</ul>
                </div>
              )}
              <div className="pb-actions pb-wide">
                <button type="button" className="pb-btn" disabled={!form.url || !!busy} onClick={check}>
                  {busy === 'preview' ? t('publishing.rss.checking', 'Checking…') : t('publishing.rss.check', 'Check feed')}
                </button>
                <button type="submit" className="pb-btn primary" disabled={!form.url || !form.platforms.length || !!busy}>
                  <FaPlus aria-hidden="true" /> {busy === 'add' ? t('publishing.rss.adding', 'Adding…') : t('publishing.rss.add', 'Follow feed')}
                </button>
              </div>
            </form>
          )}
        </section>
      )}

      {data && data.ready && data.feeds.length > 0 && (
        <section className="surface">
          <h3>{t('publishing.rss.listTitle', 'Feeds you follow')}</h3>
          <div style={{ display: 'grid', gap: 8 }}>
            {data.feeds.map((f) => (
              <article key={f.id} className={`pb-feed${f.active ? '' : ' paused'}`} data-testid="rss-feed">
                <p className="pb-feed-title">{f.title || f.url}</p>
                <div className="pb-feed-url">{f.url}</div>
                <div className="pb-feed-meta">
                  <span className={`pb-badge ${f.active ? 'ok' : 'muted'}`}>{f.active ? t('publishing.rss.active', 'Active') : t('publishing.rss.paused', 'Paused')}</span>
                  <span>{f.mode === 'schedule' ? `${t('publishing.rss.modeSchedule', 'Scheduled posts')} · ${delayLabel(f.delayMinutes)}` : t('publishing.rss.modeDraft', 'Drafts for me to review')}</span>
                  <span>{f.platforms.map((p) => NAMES[p] || p).join(', ')}</span>
                  <span>{t('publishing.rss.postedCount', '{{count}} post(s) made', { count: f.posted || 0 })}</span>
                  {f.lastCheckedOn && <span>{t('publishing.rss.lastChecked', 'Checked {{when}}', { when: when(f.lastCheckedOn) })}</span>}
                </div>
                {f.lastError && <div className="note warn">{f.lastError}</div>}
                <div className="pb-actions">
                  <button type="button" className="pb-btn" onClick={() => toggleActive(f)}>
                    {f.active ? <><FaPause aria-hidden="true" /> {t('publishing.rss.pause', 'Pause')}</> : <><FaPlay aria-hidden="true" /> {t('publishing.rss.resume', 'Resume')}</>}
                  </button>
                  <button type="button" className="pb-btn" aria-expanded={Boolean(items[f.id])} onClick={() => showItems(f)}>
                    {t('publishing.rss.recent', 'Recent items')}
                  </button>
                  <button type="button" className="pb-btn danger" onClick={() => remove(f)}>
                    <FaTrash aria-hidden="true" /> {t('publishing.rss.remove', 'Remove')}
                  </button>
                </div>
                {items[f.id] && (
                  <div className="pb-table-wrap">
                    <table className="pb-table">
                      <thead><tr><th scope="col">{t('publishing.rss.colItem', 'Item')}</th><th scope="col">{t('publishing.rss.colFound', 'Found')}</th><th scope="col">{t('publishing.rss.colResult', 'Result')}</th></tr></thead>
                      <tbody>
                        {items[f.id].length === 0 && <tr><td colSpan={3}>{t('publishing.rss.noItems', 'Nothing yet.')}</td></tr>}
                        {items[f.id].map((it, i) => (
                          <tr key={`${it.link}-${i}`}>
                            <td className="pb-content">{it.title || it.link}</td>
                            <td>{when(it.foundOn)}</td>
                            <td>
                              {outcome[it.outcome] || it.outcome}
                              {it.postId && it.approvalStatus === 'draft' && <> · <Link to={`/${tenant}/media/scheduled-posts`}>{t('publishing.rss.reviewDraft', 'Review draft')}</Link></>}
                              {it.postId && it.approvalStatus === 'in_review' && <> · <Link to={`/${tenant}/approvals`}>{t('publishing.rss.inReview', 'In review')}</Link></>}
                              {it.postId && it.scheduledOn && it.approvalStatus !== 'draft' && <> · {t('publishing.rss.publishes', 'publishes {{when}}', { when: when(it.scheduledOn) })}</>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </article>
            ))}
          </div>
          <p className="pb-ai-meta">{t('publishing.rss.footnote', 'Feeds are checked about every {{minutes}} minutes. At most 3 new items per check become posts, an hour apart; older items in a burst are skipped.', { minutes: data.pollMinutes || 30 })}</p>
        </section>
      )}

      <AppFooter buttons={buttons} />
    </div>
  );
}
